import type { PortableTextBlock } from '@portabletext/react';
import type { SanityDocument } from '@sanity/client';
import { createClient } from '@sanity/client';
import type { ReactPageFunction } from '../../js/cloudflare/types';
import type { StaticLayoutProps } from '../../js/layout/Layout';
import SanityHeaderFooterLayout from '../../js/layout/SanityHeaderFooterLayout';
import Breadcrumbs from '../../js/sanity/components/Breadcrumbs';
import Header from '../../js/sanity/components/Header';
import { Grid } from '../../js/sanity/grid/Grid';
import type { TaggedGridItemProps } from '../../js/sanity/grid/TaggedGridItem';
import TaggedGridItem, { taggedGridItemGroq } from '../../js/sanity/grid/TaggedGridItem';
import fetchPaths from '../../js/sanity/infrastructure/fetchPaths';
import { getConfig } from '../../js/sanity/infrastructure/getConfig';
import type { SanityProps } from '../../js/sanity/infrastructure/getSanityPageProps';
import { getSanityPageProps } from '../../js/sanity/infrastructure/getSanityPageProps';
import SanityPage from '../../js/sanity/infrastructure/SanityPage';
import type { SanityPageProps } from '../../js/sanity/page';
import { knownTypes } from '../../js/sanity/page';
import SimpleRichText from '../../js/sanity/richTextComponents/SimpleRichText';

interface AuthorPageResult extends SanityDocument {
    title: string;
    bio: string;
    biography?: PortableTextBlock[];
    role: string;
    email: string;
    items: TaggedGridItemProps[];
}

export const onRequestGet: ReactPageFunction<'/forfatter/[slug]', SanityProps<AuthorPageResult>> = async ({
    request,
    renderToResponse,
    next,
    ...ctx
}) => {
    const clientConfig = getConfig(ctx);

    const client = createClient(clientConfig);
    const paths = await fetchPaths(ctx.data);

    const slug = ctx.params.slug;

    const query = /* groq */ `
    *[_type == 'person' && slug.current == $slug][0] {
        bio,
        biography,
        role,
        email,
        "title": 'Artikler av ' + name,
        "items": *[_type in $knownTypes && ^._id in authors[]._ref] ${taggedGridItemGroq},
        "home": *[_type == 'homePage'][0].title
    }
    `;

    const params = { slug, knownTypes };

    // Get the page from sanity using the query and params.
    const response = await client.fetch(query, params, { filterResponse: false });

    // If sanity returns nothing, fall back to hybris page. This shouldn't happen.
    if (!response.result || response.result.items.length == 0) return next();

    try {
        const props = getSanityPageProps(clientConfig, paths.byId, response, request.url, params);

        return renderToResponse(props);
    } catch (error) {
        console.error(error);
        return next();
    }
};

export default SanityPage<AuthorPageResult>(function ({ page }) {
    return (
        <article className="sanity" lang={page.metadata?.language}>
            <Breadcrumbs breadcrumbs={[{ _id: 'home', path: '/', title: page.home }]} />
            <Header title={page.title} ingress={page.role} contactEmail={page.email} />
            <section>{page.biography ? <SimpleRichText value={page.biography} /> : <p>{page.bio}</p>}</section>
            <Grid items={page.items} component={SearchResultItem} />
        </article>
    );
});

function SearchResultItem(p: TaggedGridItemProps) {
    return <TaggedGridItem {...p} showCategory />;
}

export function StaticLayout({ props, url }: StaticLayoutProps<SanityPageProps>) {
    return <SanityHeaderFooterLayout props={props} url={url} dynamic />;
}
